import React, { useState } from "react";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

interface MultiDateCalendarProps {
  // Selected dates as YYYY-MM-DD strings
  value: string[];
  onChange: (dates: string[]) => void;
  // Dates that already have something (e.g. an existing booking), shown with a dot
  markedDates?: string[];
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Built from local parts on purpose: toISOString() would shift the day for
// timezones ahead of UTC.
const toKey = (year: number, month: number, day: number) =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export default function MultiDateCalendar({
  value,
  onChange,
  markedDates = [],
}: MultiDateCalendarProps) {
  const today = new Date();
  const [view, setView] = useState({
    year: today.getFullYear(),
    month: today.getMonth(),
  });

  const selected = new Set(value);
  const marked = new Set(markedDates);
  const todayKey = toKey(today.getFullYear(), today.getMonth(), today.getDate());

  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  // Monday-first offset for the 1st of the month
  const offset = (new Date(view.year, view.month, 1).getDay() + 6) % 7;

  const shiftMonth = (delta: number) => {
    const d = new Date(view.year, view.month + delta, 1);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };

  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next).sort());
  };

  const monthLabel = new Date(view.year, view.month, 1).toLocaleDateString(
    "en-GB",
    { month: "long", year: "numeric" },
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <IconButton size="small" onClick={() => shiftMonth(-1)}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="subtitle1" fontWeight="bold">
          {monthLabel}
        </Typography>
        <IconButton size="small" onClick={() => shiftMonth(1)}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
        }}
      >
        {WEEKDAYS.map((w) => (
          <Typography
            key={w}
            variant="caption"
            color="text.secondary"
            align="center"
          >
            {w}
          </Typography>
        ))}
        {Array.from({ length: offset }).map((_, i) => (
          <Box key={`blank-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const key = toKey(view.year, view.month, day);
          const isSelected = selected.has(key);
          return (
            <Button
              key={key}
              onClick={() => toggle(key)}
              variant={isSelected ? "contained" : "text"}
              color={isSelected ? "primary" : "inherit"}
              sx={{
                minWidth: 0,
                p: 0,
                height: 40,
                position: "relative",
                fontWeight: isSelected || key === todayKey ? 700 : 400,
                border: key === todayKey ? "1px solid" : "none",
                borderColor: "primary.main",
              }}
            >
              {day}
              {marked.has(key) && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: isSelected ? "primary.contrastText" : "warning.main",
                  }}
                />
              )}
            </Button>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1.5,
          mb: 0.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {value.length === 0
            ? "Click days to select them"
            : `${value.length} day${value.length === 1 ? "" : "s"} selected`}
        </Typography>
        {value.length > 0 && (
          <Button size="small" onClick={() => onChange([])}>
            Clear
          </Button>
        )}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {value.map((d) => (
          <Chip key={d} size="small" label={d} onDelete={() => toggle(d)} />
        ))}
      </Box>
      {markedDates.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          Orange dot = a booking already exists on that day
        </Typography>
      )}
    </Box>
  );
}
